/**
 * P2 — JSON error contract for an unknown top-level verb.
 *
 * `grid <unknown-verb> --json` (or `-o json` / `-o yaml` / `CLOUDGRID_OUTPUT`)
 * used to fall through to commander's plaintext `error: unknown command 'x'`
 * on stderr, so a machine consumer that asked for structured output got no
 * envelope. This guard runs BEFORE `program.parseAsync()`: when the first
 * top-level verb is unknown AND structured output was requested, it emits the
 * standard `{ error: { code, message } }` envelope (matching the rest of the
 * CLI's structured-error shape) to stdout and returns `true` so the caller can
 * exit non-zero. In every other case it returns `false` and does nothing —
 * commander keeps its human-facing plaintext + did-you-mean suggestion.
 *
 * Scope is the TOP-LEVEL verb only (the `grid <unknown-verb>` case named in the
 * diagnostic). An unknown SUBcommand under a known verb still takes commander's
 * default path; see the session doc for the follow-up.
 */
import type { Command } from 'commander';
import {
  serializeStructured,
  structuredErrorPayload,
  type StructuredFormat,
} from './services/output-format.js';

/** commander's own built-in verbs that are not registered in `program.commands`. */
const BUILTIN_VERBS = new Set(['help']);

/**
 * Detect the requested structured format from argv + env, mirroring
 * `resolveOutputFormat` precedence but WITHOUT throwing on a bad `-o` value
 * (an invalid format is not our concern here — commander/verb handles it).
 * Returns the format when json/yaml was explicitly requested, else null.
 */
function requestedStructuredFormat(
  args: ReadonlyArray<string>,
  env: NodeJS.ProcessEnv,
): StructuredFormat | null {
  const asFormat = (v: string | undefined): StructuredFormat | null => {
    const s = (v ?? '').trim().toLowerCase();
    return s === 'json' || s === 'yaml' ? s : null;
  };
  for (let i = 0; i < args.length; i++) {
    const a = args[i]!;
    if (a === '--json') return 'json';
    if (a === '-o' || a === '--output') {
      const f = asFormat(args[i + 1]);
      if (f) return f;
    } else if (a.startsWith('--output=')) {
      const f = asFormat(a.slice('--output='.length));
      if (f) return f;
    } else if (a.startsWith('-o=')) {
      const f = asFormat(a.slice('-o='.length));
      if (f) return f;
    }
  }
  return asFormat(env.CLOUDGRID_OUTPUT);
}

/** First non-option token — the top-level verb — or undefined if there is none. */
function firstVerb(args: ReadonlyArray<string>): string | undefined {
  for (const a of args) {
    if (a === '--') break;
    if (!a.startsWith('-')) return a;
  }
  return undefined;
}

function isKnownVerb(program: Command, verb: string): boolean {
  if (BUILTIN_VERBS.has(verb)) return true;
  return program.commands.some(
    (c) => c.name() === verb || c.aliases().includes(verb),
  );
}

/**
 * @returns `true` when it emitted an envelope (caller should exit non-zero),
 *   `false` when it did nothing (normal commander flow should proceed).
 */
export function maybeHandleUnknownVerbJson(
  program: Command,
  argv: ReadonlyArray<string> = process.argv,
  env: NodeJS.ProcessEnv = process.env,
  write: (s: string) => void = (s) => process.stdout.write(s),
  exit: (code: number) => void = (c) => process.exit(c),
): boolean {
  const args = argv.slice(2);
  const format = requestedStructuredFormat(args, env);
  if (!format) return false;
  const verb = firstVerb(args);
  if (verb === undefined || isKnownVerb(program, verb)) return false;

  const payload = structuredErrorPayload({
    code: 'UNKNOWN_COMMAND',
    message: `Unknown command '${verb}'. Run 'grid --help' to see available commands.`,
  });
  write(serializeStructured(format, payload));
  exit(1);
  return true;
}
